import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useAuth } from './AuthContext';
import api from '../lib/api';

const TokenContext = createContext(null);

const LOW_BALANCE_THRESHOLD = 10;

export function TokenProvider({ children }) {
  const { user } = useAuth();
  const [balance, setBalance] = useState(null);
  const [costs, setCosts] = useState({});
  const [hasFreeScreenplay, setHasFreeScreenplay] = useState(false);
  const [insufficientError, setInsufficientError] = useState(null);
  const [loading, setLoading] = useState(true);

  const refreshBalance = useCallback(async () => {
    try {
      const { data } = await api.get('/tokens/balance');
      setBalance(data.balance);
      setCosts(data.costs);
      setHasFreeScreenplay(!!data.has_free_screenplay);
    } catch (e) {
      // Not logged in or network issue — don't log noise
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetch balance when user auth state changes (login/logout)
  useEffect(() => {
    if (user) {
      refreshBalance();
    } else {
      setBalance(null);
    }
  }, [user, refreshBalance]);

  // Periodic balance sync every 30s to catch webhook credits / external changes
  useEffect(() => {
    const interval = setInterval(refreshBalance, 30000);
    return () => clearInterval(interval);
  }, [refreshBalance]);

  // Also refresh on tab focus (user switches back after Stripe checkout, etc.)
  useEffect(() => {
    const handleFocus = () => refreshBalance();
    window.addEventListener('focus', handleFocus);
    return () => window.removeEventListener('focus', handleFocus);
  }, [refreshBalance]);

  // Set up 402 interceptor
  useEffect(() => {
    const interceptor = api.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response?.status === 402) {
          const detail = error.response.data?.detail;
          if (detail?.error === 'insufficient_tokens') {
            setInsufficientError(detail);
            // Update local balance from server response
            if (typeof detail.balance === 'number') {
              setBalance(detail.balance);
            }
          }
        }
        return Promise.reject(error);
      }
    );
    return () => api.interceptors.response.eject(interceptor);
  }, []);

  const dismissInsufficientError = useCallback(() => {
    setInsufficientError(null);
  }, []);

  const isLowBalance = balance !== null && balance <= LOW_BALANCE_THRESHOLD;

  return (
    <TokenContext.Provider value={{
      balance,
      costs,
      loading,
      isLowBalance,
      hasFreeScreenplay,
      insufficientError,
      refreshBalance,
      dismissInsufficientError,
    }}>
      {children}
    </TokenContext.Provider>
  );
}

export function useTokens() {
  const context = useContext(TokenContext);
  if (!context) {
    throw new Error('useTokens must be used within a TokenProvider');
  }
  return context;
}
